import { DiagnosticFormData } from '../types/diagnostic';
import { RecommendedAction, Vulnerability } from './diagnosticService';
import { calculateSecurityScore } from '../utils/securityCalculator';
import securityWeights from '../config/securityWeights.json';
import { getContextualPriorities } from '../utils/contextualRecommendations';

// Mapping entre les IDs d'actions et les IDs d'équipements
const ACTION_TO_EQUIPMENT_MAP: Record<string, string> = {
  'install-reinforced-door': 'door',
  'install-alarm-system': 'alarm',
  'install-cameras': 'camera',
  'install-smart-lock': 'smartLock',
  'install-outdoor-lighting': 'lighting',
  'install-video-doorbell': 'videoDoorbell',
  'install-window-locks': 'windowLocks',
  'install-motion-sensors': 'motionSensor',
  'install-door-sensors': 'doorSensor',
  'install-safe': 'safe',
  'install-window-bars': 'bars',
  'install-intercom': 'intercom',
  'install-fog-system': 'fogSystem',
  'secure-perimeter': 'securityGate',
  // Nouveaux équipements
  'install-window-film': 'windowFilm',
  'install-door-hinges': 'doorHinges',
  'install-multipoint-lock': 'multipointLock',
  'install-door-bar': 'doorBar',
  'install-glass-break-detector': 'glassBreakDetector',
  'install-remote-surveillance': 'remoteSurveillance',
  'install-light-timer': 'lightTimer',
  'install-dummy-camera': 'dummyCamera',
  'install-security-stickers': 'securityStickers',
};

// Actions interdites aux locataires (modifications structurelles permanentes)
const STRUCTURAL_MODIFICATIONS = [
  'install-reinforced-door',
  'upgrade-door-locks',
  'install-multipoint-lock',
  'install-window-bars',
  'install-window-film',
  'install-door-hinges',
  'secure-perimeter',
  'install-perimeter-lighting',
  'secure-basement',
  'secure-garage',
  'install-window-locks'
];

// Actions nécessitant un accès extérieur direct (impossibles en parties communes)
const EXTERIOR_ACCESS_REQUIRED = [
  'install-outdoor-lighting',
  'install-video-doorbell',
  'install-cameras',
  'install-perimeter-lighting',
  'secure-perimeter',
  'upgrade-street-visibility',
  'install-fake-cameras',
  'install-dummy-camera'
];

// Actions qui nécessitent une adaptation selon le statut d'occupation
const ADAPTABLE_ACTIONS: Record<string, {
  owner: { title: string; description: string; estimatedCost: number; priority: 'high' | 'medium' | 'low' };
  tenant: { title: string; description: string; estimatedCost: number; priority: 'high' | 'medium' | 'low' };
}> = {
  'install-alarm-system': {
    owner: {
      title: 'Installer un système d\'alarme',
      description: 'Alarme anti-intrusion certifiée NF&A2P avec sirène intérieure et extérieure, installation filaire professionnelle',
      estimatedCost: 800,
      priority: 'high'
    },
    tenant: {
      title: 'Installer une alarme sans fil',
      description: 'Système d\'alarme sans fil certifié, facile à installer et démonter sans perçage, idéal pour locataires',
      estimatedCost: 400,
      priority: 'high'
    }
  },
  'install-cameras': {
    owner: {
      title: 'Installer des caméras de surveillance',
      description: 'Kit de vidéosurveillance avec enregistrement local et accès à distance, installation filaire',
      estimatedCost: 600,
      priority: 'high'
    },
    tenant: {
      title: 'Installer des caméras WiFi autonomes',
      description: 'Caméras sans fil sur batterie ou secteur, installation sans perçage, facilement démontables',
      estimatedCost: 400,
      priority: 'high'
    }
  },
  'install-outdoor-lighting': {
    owner: {
      title: 'Installer un éclairage extérieur automatique',
      description: 'Projecteurs LED avec détecteur de mouvement et temporisation, installation électrique permanente',
      estimatedCost: 250,
      priority: 'medium'
    },
    tenant: {
      title: 'Installer un éclairage extérieur solaire',
      description: 'Projecteurs solaires avec détecteur de mouvement, sans installation électrique, facilement démontables',
      estimatedCost: 150,
      priority: 'medium'
    }
  },
  'install-smart-lock': {
    owner: {
      title: 'Équiper d\'une serrure connectée',
      description: 'Serrure multipoints connectée avec contrôle à distance et historique, installation permanente',
      estimatedCost: 300,
      priority: 'medium'
    },
    tenant: {
      title: 'Équiper d\'une serrure connectée amovible',
      description: 'Serrure connectée type Yale Linus, s\'installe sur la serrure existante sans modification, démontable',
      estimatedCost: 250,
      priority: 'medium'
    }
  },
  'install-safe': {
    owner: {
      title: 'Installer un coffre-fort',
      description: 'Coffre-fort à encastrer ou à poser, certifié pour objets de valeur, fixation permanente',
      estimatedCost: 400,
      priority: 'low'
    },
    tenant: {
      title: 'Installer un coffre-fort portable',
      description: 'Coffre-fort à poser facilement transportable, fixation temporaire ou aucune fixation',
      estimatedCost: 300,
      priority: 'low'
    }
  }
};

/**
 * Filtre et adapte les actions selon le statut d'occupation et le type d'accès
 */
function filterActionsByOccupancyStatus(
  actions: RecommendedAction[],
  occupancyStatus: 'owner' | 'tenant' | '',
  formData: DiagnosticFormData
): RecommendedAction[] {
  let filteredActions = actions;

  // Filtrer les équipements extérieurs pour appartements sans accès direct
  if (formData.housingType === 'apartment' && formData.apartmentAccessType === 'common_areas') {
    filteredActions = filteredActions.filter(action => {
      if (action.id === 'install-cameras') {
        return true;
      }
      return !EXTERIOR_ACCESS_REQUIRED.includes(action.id);
    }).map(action => {
      if (action.id === 'install-cameras') {
        return {
          ...action,
          title: 'Installer des caméras intérieures',
          description: 'Caméras WiFi discrètes pour surveiller l\'intérieur de votre appartement'
        };
      }
      return action;
    });
  }

  if (occupancyStatus === 'tenant') {
    return filteredActions
      .filter(action => !STRUCTURAL_MODIFICATIONS.includes(action.id))
      .map(action => {
        if (action.id in ADAPTABLE_ACTIONS) {
          const adapted = ADAPTABLE_ACTIONS[action.id].tenant;
          return {
            ...action,
            title: adapted.title,
            description: adapted.description,
            estimatedCost: adapted.estimatedCost,
            priority: adapted.priority
          };
        }
        return action;
      });
  }

  if (occupancyStatus === 'owner') {
    return filteredActions.map(action => {
      if (action.id in ADAPTABLE_ACTIONS) {
        const adapted = ADAPTABLE_ACTIONS[action.id].owner;
        return {
          ...action,
          title: adapted.title,
          description: adapted.description,
          estimatedCost: adapted.estimatedCost,
          priority: adapted.priority
        };
      }
      return action;
    });
  }

  return filteredActions;
}

/**
 * Génère un message d'information selon le statut d'occupation
 */
export function getOccupancyStatusMessage(
  occupancyStatus: 'owner' | 'tenant' | ''
): string | null {
  if (occupancyStatus === 'tenant') {
    return "En tant que locataire, certaines recommandations structurelles ne sont pas affichées car elles nécessitent l'accord de votre propriétaire. Nous privilégions les équipements amovibles et faciles à installer.";
  }

  if (occupancyStatus === 'owner') {
    return "En tant que propriétaire, vous pouvez bénéficier de toutes les recommandations, y compris les modifications structurelles qui augmentent la valeur de votre bien.";
  }

  return null;
}

/**
 * Génère un message d'information selon le type d'accès de l'appartement
 */
export function getApartmentAccessMessage(
  housingType: 'house' | 'apartment',
  apartmentAccessType: 'direct' | 'common_areas' | ''
): string | null {
  if (housingType === 'apartment' && apartmentAccessType === 'common_areas') {
    return "Votre appartement étant accessible par des parties communes, nous avons adapté nos recommandations aux équipements installables à l'intérieur de votre logement. Les installations extérieures nécessitent généralement l'accord du syndic de copropriété.";
  }

  return null;
}

/**
 * Ajuste le score d'impact selon la qualité choisie
 */
export function adjustScoreByQuality(
  baseScore: number,
  tier: 'budget' | 'recommended' | 'premium'
): number {
  const multipliers = {
    budget: 0.75,
    recommended: 1.0,
    premium: 1.15
  };

  return Math.round(baseScore * multipliers[tier]);
}

/**
 * Augmente la priorité des actions selon le contexte
 */
function boostPriorityForContext(
  action: RecommendedAction,
  formData: DiagnosticFormData
): RecommendedAction {
  // Si appartement avec parties communes, privilégier les actions intérieures de sécurisation
  if (formData.housingType === 'apartment' && formData.apartmentAccessType === 'common_areas') {
    const interiorActionsToBoost = [
      'install-reinforced-door',
      'install-multipoint-lock',
      'install-door-bar',
      'install-door-sensors',
      'install-digital-peephole',
      'install-alarm-system',
      'install-motion-sensors'
    ];

    if (interiorActionsToBoost.includes(action.id) && action.priority !== 'high') {
      return { ...action, priority: 'high' };
    }
  }

  return action;
}

/**
 * Supprime les doublons en gardant la première occurrence de chaque action par ID
 */
function deduplicateActions(actions: RecommendedAction[]): RecommendedAction[] {
  const seen = new Set<string>();
  return actions.filter(action => {
    if (seen.has(action.id)) {
      return false;
    }
    seen.add(action.id);
    return true;
  });
}

export function generateVulnerabilities(
  formData: DiagnosticFormData,
  categoryScores: Record<string, number>
): Vulnerability[] {
  const vulnerabilities: Vulnerability[] = [];
  const isTenant = formData.occupancyStatus === 'tenant';

  if (categoryScores.protection < 50) {
    vulnerabilities.push({
      label: 'Protection physique insuffisante',
      risk: categoryScores.protection < 30 ? 'high' : 'medium',
      description: 'Vos points d\'accès (portes, fenêtres) ne sont pas suffisamment sécurisés.',
      recommendations: isTenant ? [
        'Installer une barre de sécurité amovible pour la porte',
        'Ajouter des détecteurs d\'ouverture sur portes et fenêtres',
        'Installer un système d\'alarme sans fil',
        'Utiliser un coffre-fort portable pour les objets de valeur'
      ] : [
        'Installer une porte blindée ou un bloc-porte certifié A2P',
        'Équiper les fenêtres de serrures de sécurité ou de vitrages anti-effraction',
        'Renforcer les encadrements de portes et fenêtres',
        'Installer un coffre-fort encastré pour les objets de valeur'
      ]
    });
  }

  if (categoryScores.surveillance < 50) {
    vulnerabilities.push({
      label: 'Système de surveillance limité',
      risk: categoryScores.surveillance < 30 ? 'high' : 'medium',
      description: 'Votre logement manque de dispositifs de détection d\'intrusion.',
      recommendations: isTenant ? [
        'Installer une alarme sans fil avec télésurveillance',
        'Mettre en place des caméras WiFi autonomes facilement démontables',
        'Équiper les ouvertures de détecteurs d\'ouverture sans fil',
        'Installer une sonnette vidéo connectée'
      ] : [
        'Installer un système d\'alarme connecté avec télésurveillance',
        'Mettre en place des caméras de surveillance (intérieures et extérieures)',
        'Équiper les ouvertures de détecteurs d\'ouverture',
        'Installer des détecteurs de mouvement dans les zones stratégiques'
      ]
    });
  }

  if (categoryScores.deterrence < 50) {
    vulnerabilities.push({
      label: 'Dissuasion insuffisante',
      risk: categoryScores.deterrence < 30 ? 'high' : 'medium',
      description: 'Votre logement n\'affiche pas de signes visibles de protection.',
      recommendations: isTenant ? [
        'Installer un éclairage solaire avec détecteur de mouvement',
        'Poser une signalétique de sécurité visible (autocollants amovibles)',
        'Installer une sonnette vidéo connectée à l\'entrée',
        'Utiliser des prises programmables pour simuler une présence'
      ] : [
        'Installer un éclairage extérieur avec détecteur de mouvement',
        'Poser une signalétique de sécurité visible (autocollants, panneaux)',
        'Installer une sonnette vidéo connectée à l\'entrée',
        'Mettre en place un système de simulation de présence'
      ]
    });
  }

  if (formData.hasAccessibleGarden && !formData.equipment.includes('securityGate') && !isTenant) {
    vulnerabilities.push({
      label: 'Périmètre extérieur non sécurisé',
      risk: 'medium',
      description: 'Votre jardin accessible facilite l\'approche discrète de votre logement.',
      recommendations: [
        'Installer un portail ou portillon sécurisé avec serrure robuste',
        'Mettre en place une clôture d\'au moins 1,80m de hauteur',
        'Éclairer le périmètre avec des projecteurs à détection',
        'Installer des caméras couvrant les zones d\'accès extérieur'
      ]
    });
  }

  if (formData.hasBasement && categoryScores.protection < 60 && !isTenant) {
    vulnerabilities.push({
      label: 'Dépendances vulnérables',
      risk: 'medium',
      description: 'Votre sous-sol peut constituer un point d\'entrée pour les cambrioleurs.',
      recommendations: [
        'Sécuriser l\'accès au sous-sol avec une porte renforcée',
        'Installer des soupiraux avec grilles de sécurité',
        'Équiper le sous-sol de détecteurs de mouvement',
        'Verrouiller l\'accès depuis l\'intérieur du logement'
      ]
    });
  }

  return vulnerabilities.slice(0, 3);
}

export function generateRecommendedActions(
  formData: DiagnosticFormData,
  categoryScores: Record<string, number>
): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  // ========================================
  // RÉCUPÉRATION DES RÈGLES CONTEXTUELLES
  // ========================================
  const contextualRules = getContextualPriorities(formData);
  
  // Map pour accès rapide aux priorités contextuelles
  const contextualPriorityMap = new Map<string, { priority: 'high' | 'medium' | 'low', reason: string }>();
  contextualRules.forEach(rule => {
    contextualPriorityMap.set(rule.equipmentId, { 
      priority: rule.priority, 
      reason: rule.reason 
    });
  });

  // ========================================
  // ACTIONS BASÉES SUR LES ÉQUIPEMENTS MANQUANTS
  // ========================================
  
  if (!formData.equipment.includes('door')) {
    const contextual = contextualPriorityMap.get('door');
    actions.push({
      id: 'install-reinforced-door',
      title: 'Installer une porte d\'entrée sécurisée',
      category: 'protection',
      priority: contextual?.priority || 'high',
      estimatedCost: 1500,
      scoreImpact: securityWeights.equipment.door.score,
      description: contextual?.reason || 'Porte blindée ou bloc-porte certifié A2P BP1, BP2 ou BP3 selon le niveau souhaité'
    });
  }

  if (!formData.equipment.includes('alarm')) {
    const contextual = contextualPriorityMap.get('alarm');
    actions.push({
      id: 'install-alarm-system',
      title: 'Installer un système d\'alarme',
      category: 'surveillance',
      priority: contextual?.priority || 'high',
      estimatedCost: 800,
      scoreImpact: securityWeights.equipment.alarm.score,
      description: contextual?.reason || 'Alarme anti-intrusion certifiée NF&A2P avec sirène intérieure et extérieure'
    });
  }

  if (!formData.equipment.includes('camera')) {
    const contextual = contextualPriorityMap.get('camera');
    actions.push({
      id: 'install-cameras',
      title: 'Installer des caméras de surveillance',
      category: 'surveillance',
      priority: contextual?.priority || 'high',
      estimatedCost: 600,
      scoreImpact: securityWeights.equipment.camera.score,
      description: contextual?.reason || 'Kit de vidéosurveillance avec enregistrement et accès à distance'
    });
  }

  if (!formData.equipment.includes('smartLock')) {
    actions.push({
      id: 'install-smart-lock',
      title: 'Équiper d\'une serrure connectée',
      category: 'protection',
      priority: 'medium',
      estimatedCost: 300,
      scoreImpact: securityWeights.equipment.smartLock.score,
      description: 'Serrure multipoints connectée avec contrôle à distance et historique'
    });
  }

  if (!formData.equipment.includes('lighting')) {
    const contextual = contextualPriorityMap.get('lighting');
    actions.push({
      id: 'install-outdoor-lighting',
      title: 'Installer un éclairage extérieur automatique',
      category: 'dissuasion',
      priority: contextual?.priority || 'medium',
      estimatedCost: 250,
      scoreImpact: securityWeights.equipment.lighting.score,
      description: contextual?.reason || 'Projecteurs LED avec détecteur de mouvement et temporisation'
    });
  }

  if (!formData.equipment.includes('videoDoorbell')) {
    actions.push({
      id: 'install-video-doorbell',
      title: 'Installer une sonnette vidéo',
      category: 'surveillance',
      priority: 'medium',
      estimatedCost: 200,
      scoreImpact: securityWeights.equipment.videoDoorbell.score,
      description: 'Sonnette connectée avec caméra, détection de mouvement et interphone'
    });
  }

  // Judas numérique recommandé pour les appartements en parties communes
  if (formData.housingType === 'apartment' &&
      formData.apartmentAccessType === 'common_areas' &&
      !formData.equipment.includes('digitalPeephole')) {
    actions.push({
      id: 'install-digital-peephole',
      title: 'Installer un judas numérique',
      category: 'surveillance',
      priority: 'high',
      estimatedCost: 150,
      scoreImpact: 3,
      description: 'Œilleton connecté avec écran LCD et enregistrement, alternative à la sonnette vidéo'
    });
  }

  if (!formData.equipment.includes('windowLocks')) {
    const contextual = contextualPriorityMap.get('windowLocks');
    actions.push({
      id: 'install-window-locks',
      title: 'Sécuriser les fenêtres',
      category: 'protection',
      priority: contextual?.priority || 'medium',
      estimatedCost: 150,
      scoreImpact: securityWeights.equipment.windowLocks.score,
      description: contextual?.reason || 'Serrures de fenêtres et entrebâilleurs de sécurité'
    });
  }

  if (!formData.equipment.includes('motionSensor')) {
    const contextual = contextualPriorityMap.get('motionSensor');
    actions.push({
      id: 'install-motion-sensors',
      title: 'Installer des détecteurs de mouvement',
      category: 'surveillance',
      priority: contextual?.priority || 'medium',
      estimatedCost: 180,
      scoreImpact: securityWeights.equipment.motionSensor.score,
      description: contextual?.reason || 'Détecteurs volumétriques infrarouges pour zones stratégiques'
    });
  }

  if (!formData.equipment.includes('doorSensor')) {
    const contextual = contextualPriorityMap.get('doorSensor');
    actions.push({
      id: 'install-door-sensors',
      title: 'Équiper les ouvertures de détecteurs',
      category: 'surveillance',
      priority: contextual?.priority || 'low',
      estimatedCost: 120,
      scoreImpact: securityWeights.equipment.doorSensor.score,
      description: contextual?.reason || 'Détecteurs d\'ouverture magnétiques pour portes, fenêtres et volets (protection multicouche)'
    });
  }

  if (!formData.equipment.includes('safe')) {
    actions.push({
      id: 'install-safe',
      title: 'Installer un coffre-fort',
      category: 'protection',
      priority: 'low',
      estimatedCost: 400,
      scoreImpact: securityWeights.equipment.safe.score,
      description: 'Coffre-fort à encastrer ou à poser, certifié pour objets de valeur'
    });
  }

  if (!formData.equipment.includes('bars')) {
    const contextual = contextualPriorityMap.get('bars');
    actions.push({
      id: 'install-window-bars',
      title: 'Installer des barreaux aux fenêtres',
      category: 'protection',
      priority: contextual?.priority || 'medium',
      estimatedCost: 500,
      scoreImpact: securityWeights.equipment.bars.score,
      description: contextual?.reason || 'Barreaux fixes ou amovibles pour fenêtres du rez-de-chaussée'
    });
  }

  if (!formData.equipment.includes('intercom')) {
    actions.push({
      id: 'install-intercom',
      title: 'Installer un interphone ou visiophone',
      category: 'surveillance',
      priority: 'low',
      estimatedCost: 180,
      scoreImpact: securityWeights.equipment.intercom.score,
      description: 'Système d\'interphone avec ou sans caméra pour identifier les visiteurs'
    });
  }

  if (!formData.equipment.includes('fogSystem')) {
    actions.push({
      id: 'install-fog-system',
      title: 'Installer un système de brouillard anti-intrusion',
      category: 'dissuasion',
      priority: 'low',
      estimatedCost: 1200,
      scoreImpact: securityWeights.equipment.fogSystem.score,
      description: 'Générateur de brouillard dense qui se déclenche en cas d\'intrusion'
    });
  }

  // ========================================
  // NOUVEAUX ÉQUIPEMENTS (AVEC PRIORISATION CONTEXTUELLE)
  // ========================================

  if (!formData.equipment.includes('bars') && 
      !formData.equipment.includes('windowFilm') &&
      categoryScores.protection < 60) {
    const contextual = contextualPriorityMap.get('windowFilm');
    actions.push({
      id: 'install-window-film',
      title: 'Installer un film anti-effraction sur les vitres',
      category: 'protection',
      priority: contextual?.priority || 'medium',
      estimatedCost: 300,
      scoreImpact: securityWeights.equipment.windowFilm.score,
      description: contextual?.reason || 'Film de sécurité transparent pour renforcer vos vitres'
    });
  }

  if (formData.equipment.includes('door') && 
      !formData.equipment.includes('doorHinges')) {
    actions.push({
      id: 'install-door-hinges',
      title: 'Renforcer les gonds de la porte',
      category: 'protection',
      priority: 'low',
      estimatedCost: 115,
      scoreImpact: securityWeights.equipment.doorHinges.score,
      description: 'Gonds anti-dégondage pour compléter la porte blindée'
    });
  }

  if (!formData.equipment.includes('door') && 
      !formData.equipment.includes('multipointLock')) {
    const contextual = contextualPriorityMap.get('multipointLock');
    actions.push({
      id: 'install-multipoint-lock',
      title: 'Installer une serrure multipoint',
      category: 'protection',
      priority: contextual?.priority || 'high',
      estimatedCost: 450,
      scoreImpact: securityWeights.equipment.multipointLock.score,
      description: contextual?.reason || 'Système de verrouillage à 5 points certifié A2P'
    });
  }

  if (!formData.equipment.includes('door') && 
      !formData.equipment.includes('multipointLock') &&
      !formData.equipment.includes('doorBar') &&
      categoryScores.protection < 40) {
    actions.push({
      id: 'install-door-bar',
      title: 'Ajouter une barre de sécurité',
      category: 'protection',
      priority: 'medium',
      estimatedCost: 85,
      scoreImpact: securityWeights.equipment.doorBar.score,
      description: 'Barre de blocage renforcée pour la porte d\'entrée'
    });
  }

  if (formData.equipment.includes('alarm') && 
      !formData.equipment.includes('glassBreakDetector')) {
    const contextual = contextualPriorityMap.get('glassBreakDetector');
    actions.push({
      id: 'install-glass-break-detector',
      title: 'Installer des détecteurs de bris de vitre',
      category: 'surveillance',
      priority: contextual?.priority || 'medium',
      estimatedCost: 60,
      scoreImpact: securityWeights.equipment.glassBreakDetector.score,
      description: contextual?.reason || 'Capteurs acoustiques pour détecter les intrusions par fenêtres'
    });
  }

  if (!formData.equipment.includes('remoteSurveillance')) {
    const contextual = contextualPriorityMap.get('remoteSurveillance');
    const isHighRisk = formData.crimeData?.tauxPour1000Logements && formData.crimeData.tauxPour1000Logements > 6.0;
    actions.push({
      id: 'install-remote-surveillance',
      title: 'Souscrire à un service de télésurveillance',
      category: 'surveillance',
      priority: contextual?.priority || (isHighRisk ? 'high' : 'medium'),
      estimatedCost: 300,
      scoreImpact: securityWeights.equipment.remoteSurveillance.score,
      description: contextual?.reason || 'Surveillance 24/7 avec levée de doute et intervention rapide'
    });
  }

  if (!formData.equipment.includes('lighting') && 
      !formData.equipment.includes('lightTimer')) {
    const contextual = contextualPriorityMap.get('lightTimer');
    actions.push({
      id: 'install-light-timer',
      title: 'Installer un programmateur d\'éclairage',
      category: 'dissuasion',
      priority: contextual?.priority || 'low',
      estimatedCost: 55,
      scoreImpact: securityWeights.equipment.lightTimer.score,
      description: contextual?.reason || 'Simulateur de présence automatique pour les absences'
    });
  }

  if (!formData.equipment.includes('camera') && 
      !formData.equipment.includes('dummyCamera') &&
      categoryScores.surveillance < 30) {
    actions.push({
      id: 'install-dummy-camera',
      title: 'Installer une caméra factice visible',
      category: 'dissuasion',
      priority: 'low',
      estimatedCost: 22,
      scoreImpact: securityWeights.equipment.dummyCamera.score,
      description: 'Effet dissuasif à faible coût (complément recommandé)'
    });
  }

  if (!formData.equipment.includes('securityStickers')) {
    const contextual = contextualPriorityMap.get('securityStickers');
    actions.push({
      id: 'install-security-stickers',
      title: 'Apposer une signalétique de sécurité',
      category: 'dissuasion',
      priority: contextual?.priority || 'low',
      estimatedCost: 5,
      scoreImpact: securityWeights.equipment.securityStickers.score,
      description: 'Autocollants "Alarme" ou "Vidéosurveillance" visibles'
    });
  }

  // ========================================
  // ACTIONS COMPLÉMENTAIRES
  // ========================================

  if (!formData.equipment.includes('multipointLock')) {
    actions.push({
      id: 'upgrade-door-locks',
      title: 'Installer une serrure multipoints A2P',
      category: 'protection',
      priority: categoryScores.protection < 50 ? 'high' : 'medium',
      estimatedCost: 350,
      scoreImpact: 6,
      description: 'Serrure certifiée A2P avec 3 à 7 points de fermeture'
    });
  }




  // Actions spécifiques selon le type de logement
  // Permettre les installations extérieures pour maisons et appartements avec accès direct
  const canInstallExterior =
    formData.housingType === 'house' ||
    (formData.housingType === 'apartment' && formData.apartmentAccessType === 'direct');

  if (canInstallExterior) {
    if (formData.hasAccessibleGarden && !formData.equipment.includes('securityGate')) {
      const contextual = contextualPriorityMap.get('securityGate');
      actions.push({
        id: 'secure-perimeter',
        title: 'Sécuriser le périmètre extérieur',
        category: 'protection',
        priority: contextual?.priority || 'high',
        estimatedCost: 2000,
        scoreImpact: securityWeights.equipment.securityGate.score,
        description: contextual?.reason || 'Portail/portillon sécurisé avec serrure robuste et clôture haute'
      });
    }
  }

  // Actions spécifiques aux maisons uniquement
  if (formData.housingType === 'house') {
    if (formData.hasBasement) {
      actions.push({
        id: 'secure-basement',
        title: 'Sécuriser l\'accès au sous-sol',
        category: 'protection',
        priority: 'medium',
        estimatedCost: 450,
        scoreImpact: 5,
        description: 'Porte renforcée et grilles de sécurité pour soupiraux'
      });
    }

    // Recommandation garage : SEULEMENT si le garage est une voie d'accès
    if (formData.hasGarage) {
      const isGarageAccessPoint =
        formData.housingType === 'house' ||
        formData.garageConnectedToHome === true;

      if (isGarageAccessPoint) {
        actions.push({
          id: 'secure-garage',
          title: 'Renforcer la porte de garage',
          category: 'protection',
          priority: 'medium',
          estimatedCost: 400,
          scoreImpact: 5,
          description: 'Serrure de sécurité ou motorisation avec verrouillage automatique'
        });
      }
    }
  }



  if (formData.streetLighting === 'poor') {
    actions.push({
      id: 'upgrade-street-visibility',
      title: 'Améliorer la visibilité depuis la rue',
      category: 'dissuasion',
      priority: 'medium',
      estimatedCost: 150,
      scoreImpact: 4,
      description: 'Élaguer les haies, installer des projecteurs visibles depuis la rue'
    });
  }

  // ========================================
  // ACTIONS ADMINISTRATIVES GRATUITES
  // ========================================

  const administrativeActions = [
    'insuranceDeclaration',
    'valuablePhotos', 
    'serialNumbers',
    'documentBackup',
    'detailedInventory'
  ];
  
  administrativeActions.forEach(actionId => {
    if (!formData.equipment.includes(actionId)) {
      const equipmentConfig = securityWeights.equipment[actionId as keyof typeof securityWeights.equipment];
      if (equipmentConfig) {
        actions.push({
          id: actionId,
          title: equipmentConfig.label,
          description: equipmentConfig.description,
          category: 'protection',
          priority: 'medium',
          estimatedCost: 0,
          scoreImpact: equipmentConfig.score
        });
      }
    }
  });

  // ========================================
  // BOOST DE PRIORITÉ CONTEXTUEL
  // ========================================
  const boostedActions = actions.map(action => boostPriorityForContext(action, formData));

  // ========================================
  // TRI FINAL PAR PRIORITÉ PUIS PAR IMPACT
  // ========================================
  boostedActions.sort((a, b) => {
    const priorityOrder: { [key: string]: number } = {
      'high': 1,
      'medium': 2,
      'low': 3
    };

    const priorityA = priorityOrder[a.priority as string] || 999;
    const priorityB = priorityOrder[b.priority as string] || 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return (b.scoreImpact || 0) - (a.scoreImpact || 0);
  });

  const filteredActions = filterActionsByOccupancyStatus(boostedActions, formData.occupancyStatus, formData);

  // ========================================
  // DÉDUPLICATION FINALE
  // ========================================
  const deduplicatedActions = deduplicateActions(filteredActions);

  return deduplicatedActions;
}

export function calculateProjectedScore(
  currentScore: number,
  completedActionIds: string[],
  allActions: RecommendedAction[]
): number {
  const completedActions = allActions.filter(a => completedActionIds.includes(a.id));
  
  const totalImpact = completedActions.reduce((sum, action) => {
    const baseImpact = action.scoreImpact;
    const adjustedImpact = action.selectedTier 
      ? adjustScoreByQuality(baseImpact, action.selectedTier)
      : baseImpact;
    return sum + adjustedImpact;
  }, 0);

  const projectedScore = Math.min(securityWeights.globalScoreCap, currentScore + totalImpact);
  return Math.round(projectedScore);
}